// ---------------------------------------------------------------------------
// MessageReactions — reaction buttons rendered below each assistant message.
// Used as assistantMessage.components.Footer in the Thread config.
// ---------------------------------------------------------------------------

import { useState, useCallback } from 'react'
import { useMessageRuntime } from '@assistant-ui/react'
import { ThumbsUp, ThumbsDown, Copy, MoreHorizontal, Check, RefreshCw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useChatCardsCopy } from './chat-cards-copy'

type Reaction = 'positive' | 'negative' | null

export function MessageReactions() {
  const copy = useChatCardsCopy().reactions
  const [reaction, setReaction] = useState<Reaction>(null)
  const [copied, setCopied] = useState(false)
  const messageRuntime = useMessageRuntime()

  const handleCopy = useCallback(async () => {
    try {
      const state = messageRuntime.getState()
      const textParts = state.content
        .filter((p): p is Extract<typeof p, { type: 'text' }> => p.type === 'text')
        .map((p) => p.text)
        .join('\n')
      await navigator.clipboard.writeText(textParts)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // clipboard API may fail in some contexts
    }
  }, [messageRuntime])

  const handleReaction = useCallback((type: 'positive' | 'negative') => {
    setReaction((prev) => (prev === type ? null : type))
  }, [])

  return (
    <div className="flex items-center gap-1 mt-1 opacity-0 hover:opacity-100 [.aui-assistant-message-root:hover_&]:opacity-100 [.aui-assistant-message-root:last-child_&]:opacity-100 transition-opacity">
      <button
        type="button"
        onClick={() => handleReaction('positive')}
        className={cn(
          'inline-flex items-center justify-center rounded-md p-1 text-xs transition-colors hover:bg-accent',
          reaction === 'positive'
            ? 'text-foreground bg-muted'
            : 'text-muted-foreground',
        )}
        title={copy.helpful}
      >
        <ThumbsUp className="h-3.5 w-3.5" />
      </button>

      <button
        type="button"
        onClick={() => messageRuntime.reload()}
        className="inline-flex items-center justify-center rounded-md p-1 text-xs text-muted-foreground transition-colors hover:bg-accent"
        title={copy.regenerate}
      >
        <RefreshCw className="h-3.5 w-3.5" />
      </button>

      <button
        type="button"
        onClick={() => handleReaction('negative')}
        className={cn(
          'inline-flex items-center justify-center rounded-md p-1 text-xs transition-colors hover:bg-accent',
          reaction === 'negative'
            ? 'text-destructive bg-destructive/10'
            : 'text-muted-foreground',
        )}
        title={copy.notHelpful}
      >
        <ThumbsDown className="h-3.5 w-3.5" />
      </button>

      <button
        type="button"
        onClick={handleCopy}
        className="inline-flex items-center justify-center rounded-md p-1 text-xs text-muted-foreground transition-colors hover:bg-accent"
        title={copied ? copy.copied : copy.copy}
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-foreground" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
      </button>

      <button
        type="button"
        className="inline-flex items-center justify-center rounded-md p-1 text-xs text-muted-foreground transition-colors hover:bg-accent"
        title={copy.more}
        onClick={() => {
          /* placeholder for future dropdown */
        }}
      >
        <MoreHorizontal className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
